// src/viqai/modal-manager.js
// ─── Gestionnaire de verrou de scroll ───────────────────────────────────────
// CAUSE DU GEL. L'espace VIQ.AI pose `html.viq-app-html, body.viq-app` avec
// `height: 100%; overflow: hidden` — indispensable pour que la fenêtre ne
// bouge pas et que le composer reste visible. Mais les modales héritées
// (Dashboard Pro, Dossiers, Mon espace, Admin…) sont des surfaces pleine page
// qui s'appuient sur le défilement du document. Avec le body verrouillé, une
// modale plus haute que l'écran s'affiche mais ne défile plus : sa moitié
// basse — y compris certains boutons de fermeture — devient inatteignable.
// De l'extérieur, l'application « ne répond plus ».
//
// La rustine évidente (retirer overflow:hidden) ferait revenir le composer
// sous le bord de l'écran. La bonne réponse est un COMPTEUR : le verrou n'est
// posé que lorsque aucune modale n'est ouverte, et il est repris dès que la
// dernière se ferme. Un compteur, pas un booléen, parce qu'une modale peut en
// ouvrir une autre (Dossier → charge → facture) : avec un booléen, la
// fermeture de la seconde reverrouillerait alors que la première est encore là.

let depth = 0;
const listeners = new Set();

function applyLock() {
  if (typeof document === "undefined") return;
  const locked = depth === 0;
  document.body.classList.toggle("viq-app", locked && document.body.dataset.viqApp === "1");
  document.documentElement.classList.toggle("viq-app-html", locked && document.body.dataset.viqApp === "1");
  // Marqueur lisible en inspection : savoir d'un coup d'œil combien de
  // surfaces sont empilées quand un utilisateur signale un écran figé.
  document.body.dataset.viqModalDepth = String(depth);
  listeners.forEach(fn => { try { fn(depth); } catch (e) {} });
}

// Déclare que l'espace VIQ.AI est monté : sans ce marqueur, le verrou ne
// s'applique jamais et le site public garde son défilement normal.
export function registerViqShell(active) {
  if (typeof document === "undefined") return;
  document.body.dataset.viqApp = active ? "1" : "0";
  applyLock();
}

export function pushModal() { depth += 1; applyLock(); return depth; }
export function popModal() { depth = Math.max(0, depth - 1); applyLock(); return depth; }
export function getModalDepth() { return depth; }

// Remise à zéro de sécurité. Si un composant est démonté sans passer par son
// nettoyage — une erreur de rendu, une navigation brutale — le compteur reste
// positif et le verrou n'est jamais repris. Cette porte de sortie évite qu'un
// bug isolé laisse l'application dans un état inutilisable.
export function resetModalDepth() { depth = 0; applyLock(); }

export function onModalDepthChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

// Hook : à appeler avec l'état d'ouverture d'une modale.
export function useModalLock(isOpen, React) {
  const { useEffect } = React;
  useEffect(() => {
    if (!isOpen) return;
    pushModal();
    return () => { popModal(); };
  }, [isOpen]);
}
